import { flatMap, some, sumBy } from 'lodash';
import { someValueIsNull } from '../../../../helpers/arrayHelpers';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { parseNumberOrNull } from '../../../../helpers/stringHelpers';
import { floorOutputFormulaTypeEn1264 } from '../../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import { FloorLevelCode } from '../../../shared/quote-or-design-form/quoteOrDesignFormData';
import { DownlossTypeCode } from '../../../shared/quote-or-design-form/rooms/heatLoss/downlossTypeCode';
import { RoomFloorHeatLossDetailsFormModel } from '../../../shared/quote-or-design-form/rooms/heatLoss/roomFloorHeatLossDetailsFormModel';
import { RoomRoofHeatLossDetailsFormModel } from '../../../shared/quote-or-design-form/rooms/heatLoss/roomRoofHeatLossDetailsFormModel';
import { RoomWallHeatLossDetailsFormModel } from '../../../shared/quote-or-design-form/rooms/heatLoss/roomWallHeatLossDetailsFormModel';
import { RoomFormModel } from '../../../shared/quote-or-design-form/rooms/roomFormModel';
import { UnitFloorElementFormModel } from '../../../shared/quote-or-design-form/units/heatLoss/unitFloorElementFormModel';
import { UnitRoofElementFormModel } from '../../../shared/quote-or-design-form/units/heatLoss/unitRoofElementFormModel';
import { UnitWallElementFormModel } from '../../../shared/quote-or-design-form/units/heatLoss/unitWallElementFormModel';
import { UnitFormModel } from '../../../shared/quote-or-design-form/units/unitFormModel';
import { DesignFormModel } from '../designFormModel';

export const getDesignCalculationsInputs = (formModel: DesignFormModel): DesignCalculationsInputs => {
  const units = formModel.units
    .map((unit, unitIndex) => getDesignCalculationsInputsForUnit(unit, unitIndex))
    .filter(u => u != null) as Array<UnitDesignCalculationsInputs>;

  return {
    units,
  };
};

const getDesignCalculationsInputsForUnit = (
  unit: UnitFormModel,
  unitIndex: number,
): UnitDesignCalculationsInputs | null => {
  if (unit.heatLossDetails == null) {
    return null;
  }

  const unitHeatLossDetails = unit.heatLossDetails;

  const generalAllowanceFactorPercentage = parseNumberOrNull(unitHeatLossDetails.generalAllowanceFactorPercentage);
  const exposureFactorPercentage = unitHeatLossDetails.exposureFactorPercentage;
  const propertyFootprintInMetresSquared = parseNumberOrNull(unitHeatLossDetails.propertyFootprintInMetresSquared);
  const propertyExternalPerimeterInMetres = parseNumberOrNull(unitHeatLossDetails.propertyExternalPerimeterInMetres);
  const unitExternalAirTemperatureInDegreesCelsius = parseNumberOrNull(
    unitHeatLossDetails.externalAirTemperatureInDegreesCelsius,
  );

  if (
    someValueIsNull([
      generalAllowanceFactorPercentage,
      exposureFactorPercentage,
      propertyFootprintInMetresSquared,
      propertyExternalPerimeterInMetres,
      unitExternalAirTemperatureInDegreesCelsius,
    ])
  ) {
    return null;
  }

  const { wallElements, roofElements, floorElements } = unit.heatLossDetails;

  const wallElementInputs = wallElements
    .map(getDesignCalculationsInputsForWallElement)
    .filter(inputs => inputs != null) as Array<WallElementDesignCalculationsInputs>;

  if (wallElementInputs.length < wallElements.length) {
    return null;
  }

  const roofElementInputs = roofElements
    .map(getDesignCalculationsInputsForRoofElement)
    .filter(inputs => inputs != null) as Array<RoofElementDesignCalculationsInputs>;

  if (roofElementInputs.length < roofElements.length) {
    return null;
  }

  const floorElementInputs = floorElements
    .map(getDesignCalculationsInputsForFloorElement)
    .filter(inputs => inputs != null) as Array<FloorElementDesignCalculationsInputs>;

  if (floorElementInputs.length < floorElements.length) {
    return null;
  }

  const rooms = flatMap(unit.manifolds, (manifold, manifoldIndex) => {
    const roomIsWithinUnderfloorHeatingManifold = !manifold.isRadiator;

    return (manifold.rooms || []).map((room, roomIndex): RoomDesignCalculationsInputs | null =>
      getDesignCalculationsInputsForRoom(
        room,
        roomIndex,
        manifoldIndex,
        roomIsWithinUnderfloorHeatingManifold,
        manifold.designManifoldDetails?.flowTemperatureInDegreesCelsius ?? '',
      ),
    );
  }).filter(r => r != null) as Array<RoomDesignCalculationsInputs>;

  const totalNumberOfRoomsInUnit = sumBy(unit.manifolds, m => (m.rooms || []).length);

  if (rooms.length < totalNumberOfRoomsInUnit) {
    return null;
  }

  return {
    unitIndex,
    generalAllowanceFactorPercentage: assertNotNull(generalAllowanceFactorPercentage),
    exposureFactorPercentage: assertNotNull(exposureFactorPercentage),
    propertyFootprintInMetresSquared: assertNotNull(propertyFootprintInMetresSquared),
    propertyExternalPerimeterInMetres: assertNotNull(propertyExternalPerimeterInMetres),
    unitExternalAirTemperatureInDegreesCelsius: assertNotNull(unitExternalAirTemperatureInDegreesCelsius),
    wallElements: wallElementInputs,
    roofElements: roofElementInputs,
    floorElements: floorElementInputs,
    rooms,
  };
};

const getDesignCalculationsInputsForWallElement = (
  wallElement: UnitWallElementFormModel,
  wallElementIndex: number,
): WallElementDesignCalculationsInputs | null => {
  const wallThermalTransmittance = parseNumberOrNull(wallElement.wallThermalTransmittance);
  const windowThermalTransmittance = parseNumberOrNull(wallElement.windowThermalTransmittance);
  const doorThermalTransmittance = parseNumberOrNull(wallElement.doorThermalTransmittance);

  if (someValueIsNull([wallThermalTransmittance, windowThermalTransmittance, doorThermalTransmittance])) {
    return null;
  }

  return {
    wallElementIndex,
    wallThermalTransmittance: assertNotNull(wallThermalTransmittance),
    windowThermalTransmittance: assertNotNull(windowThermalTransmittance),
    doorThermalTransmittance: assertNotNull(doorThermalTransmittance),
  };
};

const getDesignCalculationsInputsForRoofElement = (
  roofElement: UnitRoofElementFormModel,
  roofElementIndex: number,
): RoofElementDesignCalculationsInputs | null => {
  const roofThermalTransmittance = parseNumberOrNull(roofElement.roofThermalTransmittance);
  const skylightThermalTransmittance = parseNumberOrNull(roofElement.skylightThermalTransmittance);

  if (someValueIsNull([roofThermalTransmittance, skylightThermalTransmittance])) {
    return null;
  }

  return {
    roofElementIndex,
    roofThermalTransmittance: assertNotNull(roofThermalTransmittance),
    skylightThermalTransmittance: assertNotNull(skylightThermalTransmittance),
  };
};

const getDesignCalculationsInputsForFloorElement = (
  floorElement: UnitFloorElementFormModel,
  floorElementIndex: number,
): FloorElementDesignCalculationsInputs | null => {
  const floorThermalTransmittance = parseNumberOrNull(floorElement.floorThermalTransmittance);

  if (someValueIsNull([floorThermalTransmittance])) {
    return null;
  }

  return {
    floorElementIndex,
    floorThermalTransmittance: assertNotNull(floorThermalTransmittance),
  };
};

const getDesignCalculationsInputsForRoom = (
  room: RoomFormModel,
  roomIndex: number,
  manifoldIndex: number,
  roomIsWithinUnderfloorHeatingManifold: boolean,
  manifoldFlowTemperatureInDegreesCelsius: string,
): RoomDesignCalculationsInputs | null => {
  const roomTemperatureInDegreesCelsius = parseNumberOrNull(room.roomTemperature);
  const maximumRoomTemperatureInDegreesCelsius = parseNumberOrNull(
    room.designRoomDetails?.maximumTemperatureInDegreesCelsius ?? '',
  );

  const floorLevelCode = room.floorLevelCode;

  const roomAreaInMetresSquared = parseNumberOrNull(room.areaInMetresSquared);
  const voidAreaInMetresSquared = parseNumberOrNull(room.designRoomDetails?.voidAreaInMetresSquared ?? '0') || 0;

  const unheatedAreaInMetresSquared =
    parseNumberOrNull(room.designRoomDetails?.unheatedAreaInMetresSquared ?? '0') || 0;

  const peripheralAreaInMetresSquared =
    parseNumberOrNull(room.designRoomDetails?.peripheralAreaInMetresSquared ?? '') || 0;

  const flowTemperatureInDegreesCelsius = parseNumberOrNull(manifoldFlowTemperatureInDegreesCelsius);

  const temperatureDeltaInDegreesCelsius = parseNumberOrNull(
    room.designRoomDetails?.temperatureDeltaInDegreesCelsius ?? '',
  );

  if (room.heatLossDetails == null) {
    return null;
  }

  const roomHeatLossDetails = room.heatLossDetails;
  const ceilingHeightInMetres = parseNumberOrNull(roomHeatLossDetails.ceilingHeightInMetres);
  const numberOfAirChangesPerHour = parseNumberOrNull(roomHeatLossDetails.numberOfAirChangesPerHour);

  const mechanicalVentilationHeatRecoveryEfficiencyPercentage = parseNumberOrNull(
    roomHeatLossDetails.mechanicalVentilationHeatRecoveryEfficiencyPercentage,
  );

  const floorCoveringThermalResistancePerMillimetre = parseNumberOrNull(
    roomHeatLossDetails.floorCoveringThermalResistancePerMillimetre,
  );

  const floorCoveringThicknessInMillimetres = parseNumberOrNull(
    roomHeatLossDetails.floorCoveringThicknessInMillimetres,
  );

  const deckingThermalResistancePerMillimetre = parseNumberOrNull(
    roomHeatLossDetails.deckingThermalResistancePerMillimetre,
  );

  const deckingThicknessInMillimetres = parseNumberOrNull(roomHeatLossDetails.deckingThicknessInMillimetres);

  const underlayThermalResistancePerMillimetre = parseNumberOrNull(
    roomHeatLossDetails.underlayThermalResistancePerMillimetre,
  );

  const underlayThicknessInMillimetres = parseNumberOrNull(roomHeatLossDetails.underlayThicknessInMillimetres);

  const floorConstruction = room.floorConstruction;

  const userEnteredTotalHeatLossInWatts = room.heatLossDetails.overrideTotalHeatLossInWatts
    ? parseNumberOrNull(room.heatLossDetails.userEnteredTotalHeatLossInWatts)
    : null;

  let pipeDetails: PipeDetails | null = null;

  const hasRadiator = !roomIsWithinUnderfloorHeatingManifold;

  const hasSkirtingHeating =
    roomIsWithinUnderfloorHeatingManifold && floorConstruction != null && floorConstruction.isSkirtingHeating;

  const hasElectricUnderfloorHeating =
    roomIsWithinUnderfloorHeatingManifold && floorConstruction != null && floorConstruction.isElectricUnderfloorHeating;

  const hasHotWaterUnderfloorHeating =
    roomIsWithinUnderfloorHeatingManifold &&
    floorConstruction != null &&
    !floorConstruction.isSkirtingHeating &&
    !floorConstruction.isElectricUnderfloorHeating;

  if (
    roomIsWithinUnderfloorHeatingManifold &&
    floorConstruction != null &&
    !floorConstruction.isElectricUnderfloorHeating &&
    !floorConstruction.isSkirtingHeating
  ) {
    const floorConstructionPipeSpacingInMillimetres = floorConstruction?.pipeSpacingInMillimetres ?? null;
    const floorConstructionPipeDiameterInMillimetres = floorConstruction?.pipeDiameterInMillimetres ?? null;
    const estimatedLengthOfEachPipeInMetres = room.designRoomDetails?.estimatedLengthOfEachPipeInMetres;
    const drawnLengthInMetres = parseNumberOrNull(room.designRoomDetails?.drawnLengthInMetres ?? '');
    const verticalRunbackInMetres = parseNumberOrNull(room.designRoomDetails?.verticalRunbackInMetres ?? '');
    const numberOfPorts = parseNumberOrNull(room.numberOfPorts ?? '');
    const pipesPerPort = parseNumberOrNull(room.designRoomDetails?.pipesPerPort ?? '');

    if (
      someValueIsNull([
        floorConstructionPipeSpacingInMillimetres,
        floorConstructionPipeDiameterInMillimetres,
        estimatedLengthOfEachPipeInMetres,
        verticalRunbackInMetres,
        numberOfPorts,
        pipesPerPort,
      ])
    ) {
      return null;
    }

    pipeDetails = {
      floorConstructionPipeSpacingInMillimetres: assertNotNull(floorConstructionPipeSpacingInMillimetres),
      floorConstructionPipeDiameterInMillimetres: assertNotNull(floorConstructionPipeDiameterInMillimetres),
      estimatedLengthOfEachPipeInMetres: assertNotNull(estimatedLengthOfEachPipeInMetres),
      drawnLengthInMetres,
      verticalRunbackInMetres: assertNotNull(verticalRunbackInMetres),
      numberOfPorts: assertNotNull(numberOfPorts),
      pipesPerPort: assertNotNull(pipesPerPort),
    };
  }

  const mainFloorOutputFormulaType = floorConstruction?.mainFloorOutputFormulaType ?? null;
  const mainFloorOutputFormulaCoefficientA = floorConstruction?.mainFloorOutputFormulaCoefficientA ?? null;
  const mainFloorOutputFormulaCoefficientB = floorConstruction?.mainFloorOutputFormulaCoefficientB ?? null;
  const mainFloorOutputFormulaCoefficientC = floorConstruction?.mainFloorOutputFormulaCoefficientC ?? null;
  const mainFloorOutputFormulaCoefficientD = floorConstruction?.mainFloorOutputFormulaCoefficientD ?? null;
  const mainFloorOutputFormulaCoefficientE = floorConstruction?.mainFloorOutputFormulaCoefficientE ?? null;
  const mainFloorOutputFormulaCoefficientF = floorConstruction?.mainFloorOutputFormulaCoefficientF ?? null;
  const mainFloorOutputFormulaCoefficientG = floorConstruction?.mainFloorOutputFormulaCoefficientG ?? null;

  const peripheralFloorOutputFormulaType = floorConstruction?.peripheralFloorOutputFormulaType ?? null;
  const peripheralFloorOutputFormulaCoefficientA = floorConstruction?.peripheralFloorOutputFormulaCoefficientA ?? null;
  const peripheralFloorOutputFormulaCoefficientB = floorConstruction?.peripheralFloorOutputFormulaCoefficientB ?? null;
  const peripheralFloorOutputFormulaCoefficientC = floorConstruction?.peripheralFloorOutputFormulaCoefficientC ?? null;
  const peripheralFloorOutputFormulaCoefficientD = floorConstruction?.peripheralFloorOutputFormulaCoefficientD ?? null;
  const peripheralFloorOutputFormulaCoefficientE = floorConstruction?.peripheralFloorOutputFormulaCoefficientE ?? null;
  const peripheralFloorOutputFormulaCoefficientF = floorConstruction?.peripheralFloorOutputFormulaCoefficientF ?? null;
  const peripheralFloorOutputFormulaCoefficientG = floorConstruction?.peripheralFloorOutputFormulaCoefficientG ?? null;

  const screedConductivityInWattsPerMetreKelvin = parseNumberOrNull(
    room.designRoomDetails?.screedDetails?.screedConductivity ?? '',
  );

  const thicknessOfScreedAbovePipeInMillimetres = parseNumberOrNull(
    room.designRoomDetails?.screedDetails?.thicknessOfScreedAbovePipeInMillimetres ?? '',
  );

  if (
    (mainFloorOutputFormulaType === floorOutputFormulaTypeEn1264 ||
      peripheralFloorOutputFormulaType === floorOutputFormulaTypeEn1264) &&
    (screedConductivityInWattsPerMetreKelvin == null || thicknessOfScreedAbovePipeInMillimetres == null)
  ) {
    return null;
  }

  if (
    someValueIsNull([
      roomTemperatureInDegreesCelsius,
      maximumRoomTemperatureInDegreesCelsius,
      floorLevelCode,
      roomAreaInMetresSquared,
      voidAreaInMetresSquared,
      unheatedAreaInMetresSquared,
      peripheralAreaInMetresSquared,
      flowTemperatureInDegreesCelsius,
      temperatureDeltaInDegreesCelsius,
      ceilingHeightInMetres,
      numberOfAirChangesPerHour,
      mechanicalVentilationHeatRecoveryEfficiencyPercentage,
      floorCoveringThermalResistancePerMillimetre,
      floorCoveringThicknessInMillimetres,
      deckingThermalResistancePerMillimetre,
      deckingThicknessInMillimetres,
      underlayThermalResistancePerMillimetre,
      underlayThicknessInMillimetres,
    ])
  ) {
    return null;
  }

  if (assertNotNull(roomTemperatureInDegreesCelsius) > assertNotNull(maximumRoomTemperatureInDegreesCelsius)) {
    return null;
  }

  const roomWallDesignCalculationsInputs = roomHeatLossDetails.walls.map(
    (roomWall, wallElementIndex): RoomWallDesignCalculationsInputs | null =>
      getDesignCalculationsInputsForRoomWall(roomWall, wallElementIndex),
  );

  if (some(roomWallDesignCalculationsInputs, w => w == null)) {
    return null;
  }

  const roomRoofDesignCalculationsInputs = roomHeatLossDetails.roofs.map(
    (roomRoof, roofElementIndex): RoomRoofDesignCalculationsInputs | null =>
      getDesignCalculationsInputsForRoomRoof(roomRoof, roofElementIndex),
  );

  if (some(roomRoofDesignCalculationsInputs, r => r == null)) {
    return null;
  }

  const roomFloorDesignCalculationsInputs = roomHeatLossDetails.floors.map(
    (roomFloor, floorElementIndex): RoomFloorDesignCalculationsInputs | null =>
      getDesignCalculationsInputsForRoomFloor(roomFloor, floorElementIndex),
  );

  if (some(roomFloorDesignCalculationsInputs, f => f == null)) {
    return null;
  }

  return {
    manifoldIndex,
    roomIndex,
    hasHotWaterUnderfloorHeating,
    hasElectricUnderfloorHeating,
    hasSkirtingHeating,
    hasRadiator,
    roomTemperatureInDegreesCelsius: assertNotNull(roomTemperatureInDegreesCelsius),
    maximumRoomTemperatureInDegreesCelsius: assertNotNull(maximumRoomTemperatureInDegreesCelsius),
    floorLevelCode: assertNotNull(floorLevelCode),
    roomAreaInMetresSquared: assertNotNull(roomAreaInMetresSquared),
    voidAreaInMetresSquared: assertNotNull(voidAreaInMetresSquared),
    unheatedAreaInMetresSquared: assertNotNull(unheatedAreaInMetresSquared),
    peripheralAreaInMetresSquared: assertNotNull(peripheralAreaInMetresSquared),
    ceilingHeightInMetres: assertNotNull(ceilingHeightInMetres),
    numberOfAirChangesPerHour: assertNotNull(numberOfAirChangesPerHour),
    mechanicalVentilationHeatRecoveryEfficiencyPercentage: assertNotNull(
      mechanicalVentilationHeatRecoveryEfficiencyPercentage,
    ),
    floorCoveringThermalResistancePerMillimetre: assertNotNull(floorCoveringThermalResistancePerMillimetre),
    floorCoveringThicknessInMillimetres: assertNotNull(floorCoveringThicknessInMillimetres),
    deckingThermalResistancePerMillimetre: assertNotNull(deckingThermalResistancePerMillimetre),
    deckingThicknessInMillimetres: assertNotNull(deckingThicknessInMillimetres),
    underlayThermalResistancePerMillimetre: assertNotNull(underlayThermalResistancePerMillimetre),
    underlayThicknessInMillimetres: assertNotNull(underlayThicknessInMillimetres),
    flowTemperatureInDegreesCelsius: assertNotNull(flowTemperatureInDegreesCelsius),
    temperatureDeltaInDegreesCelsius: assertNotNull(temperatureDeltaInDegreesCelsius),
    mainFloorOutputFormulaDetails: {
      formulaType: mainFloorOutputFormulaType,
      coefficientA: mainFloorOutputFormulaCoefficientA,
      coefficientB: mainFloorOutputFormulaCoefficientB,
      coefficientC: mainFloorOutputFormulaCoefficientC,
      coefficientD: mainFloorOutputFormulaCoefficientD,
      coefficientE: mainFloorOutputFormulaCoefficientE,
      coefficientF: mainFloorOutputFormulaCoefficientF,
      coefficientG: mainFloorOutputFormulaCoefficientG,
    },
    peripheralFloorOutputFormulaDetails: {
      formulaType: peripheralFloorOutputFormulaType,
      coefficientA: peripheralFloorOutputFormulaCoefficientA,
      coefficientB: peripheralFloorOutputFormulaCoefficientB,
      coefficientC: peripheralFloorOutputFormulaCoefficientC,
      coefficientD: peripheralFloorOutputFormulaCoefficientD,
      coefficientE: peripheralFloorOutputFormulaCoefficientE,
      coefficientF: peripheralFloorOutputFormulaCoefficientF,
      coefficientG: peripheralFloorOutputFormulaCoefficientG,
    },
    screedConductivityInWattsPerMetreKelvin,
    thicknessOfScreedAbovePipeInMillimetres,
    userEnteredTotalHeatLossInWatts,
    pipeDetails,
    walls: roomWallDesignCalculationsInputs as Array<RoomWallDesignCalculationsInputs>,
    roofs: roomRoofDesignCalculationsInputs as Array<RoomRoofDesignCalculationsInputs>,
    floors: roomFloorDesignCalculationsInputs as Array<RoomFloorDesignCalculationsInputs>,
  };
};

const parseNumberOrNullOrZeroForRoomHeatLossData = (value: string, reference: number | null): number | null =>
  parseNumberOrNull(value) ?? (reference === 0 ? 0 : null);

const getDesignCalculationsInputsForRoomWall = (
  roomWall: RoomWallHeatLossDetailsFormModel,
  wallElementIndex: number,
): RoomWallDesignCalculationsInputs | null => {
  const outsideWallLengthInMetres = parseNumberOrNull(roomWall.outsideWallLengthInMetres);
  const additionalWallAreaInMetresSquared = parseNumberOrNullOrZeroForRoomHeatLossData(
    roomWall.additionalWallAreaInMetresSquared,
    outsideWallLengthInMetres,
  );
  const windowAreaInMetresSquared = parseNumberOrNullOrZeroForRoomHeatLossData(
    roomWall.windowAreaInMetresSquared,
    outsideWallLengthInMetres,
  );
  const doorAreaInMetresSquared = parseNumberOrNullOrZeroForRoomHeatLossData(
    roomWall.doorAreaInMetresSquared,
    outsideWallLengthInMetres,
  );
  const externalAirTemperatureInDegreesCelsius = parseNumberOrNullOrZeroForRoomHeatLossData(
    roomWall.externalAirTemperatureInDegreesCelsius,
    outsideWallLengthInMetres,
  );

  if (
    someValueIsNull([
      outsideWallLengthInMetres,
      additionalWallAreaInMetresSquared,
      windowAreaInMetresSquared,
      doorAreaInMetresSquared,
      externalAirTemperatureInDegreesCelsius,
    ])
  ) {
    return null;
  }

  return {
    wallElementIndex,
    outsideWallLengthInMetres: assertNotNull(outsideWallLengthInMetres),
    additionalWallAreaInMetresSquared: assertNotNull(additionalWallAreaInMetresSquared),
    windowAreaInMetresSquared: assertNotNull(windowAreaInMetresSquared),
    doorAreaInMetresSquared: assertNotNull(doorAreaInMetresSquared),
    externalAirTemperatureInDegreesCelsius: assertNotNull(externalAirTemperatureInDegreesCelsius),
  };
};

const getDesignCalculationsInputsForRoomRoof = (
  roomRoof: RoomRoofHeatLossDetailsFormModel,
  roofElementIndex: number,
): RoomRoofDesignCalculationsInputs | null => {
  const percentageOfRoomArea = parseNumberOrNull(roomRoof.roomAreaCoveredPercentage);
  const maximumVaultHeightInMetres = parseNumberOrNullOrZeroForRoomHeatLossData(
    roomRoof.maximumVaultHeightInMetres,
    percentageOfRoomArea,
  );
  const skylightAreaInMetresSquared = parseNumberOrNullOrZeroForRoomHeatLossData(
    roomRoof.skylightAreaInMetresSquared,
    percentageOfRoomArea,
  );
  const temperatureAboveInDegreesCelsius = parseNumberOrNullOrZeroForRoomHeatLossData(
    roomRoof.temperatureAboveInDegreesCelsius,
    percentageOfRoomArea,
  );
  const additionalRoomVolumeInMetresCubed = parseNumberOrNullOrZeroForRoomHeatLossData(
    roomRoof.additionalRoomVolumeInMetresCubed,
    percentageOfRoomArea,
  );

  if (
    someValueIsNull([
      maximumVaultHeightInMetres,
      percentageOfRoomArea,
      skylightAreaInMetresSquared,
      temperatureAboveInDegreesCelsius,
      additionalRoomVolumeInMetresCubed,
    ])
  ) {
    return null;
  }

  return {
    roofElementIndex,
    maximumVaultHeightInMetres: assertNotNull(maximumVaultHeightInMetres),
    percentageOfRoomArea: assertNotNull(percentageOfRoomArea),
    skylightAreaInMetresSquared: assertNotNull(skylightAreaInMetresSquared),
    temperatureAboveInDegreesCelsius: assertNotNull(temperatureAboveInDegreesCelsius),
    additionalRoomVolumeInMetresCubed: assertNotNull(additionalRoomVolumeInMetresCubed),
  };
};

const getDesignCalculationsInputsForRoomFloor = (
  roomFloor: RoomFloorHeatLossDetailsFormModel,
  floorElementIndex: number,
): RoomFloorDesignCalculationsInputs | null => {
  const percentageOfRoomArea = parseNumberOrNull(roomFloor.roomAreaPercentage);
  const downlossTypeCode = roomFloor.downlossTypeCode;
  const temperatureBelowInDegreesCelsius = parseNumberOrNullOrZeroForRoomHeatLossData(
    roomFloor.temperatureBelowInDegreesCelsius,
    percentageOfRoomArea,
  );

  if (someValueIsNull([percentageOfRoomArea, downlossTypeCode, temperatureBelowInDegreesCelsius])) {
    return null;
  }

  return {
    floorElementIndex,
    percentageOfRoomArea: assertNotNull(percentageOfRoomArea),
    downlossTypeCode: assertNotNull(downlossTypeCode),
    temperatureBelowInDegreesCelsius: assertNotNull(temperatureBelowInDegreesCelsius),
  };
};

export type DesignCalculationsInputs = {
  units: Array<UnitDesignCalculationsInputs>;
};

export type UnitDesignCalculationsInputs = {
  unitIndex: number;
  generalAllowanceFactorPercentage: number;
  exposureFactorPercentage: number;
  propertyFootprintInMetresSquared: number;
  propertyExternalPerimeterInMetres: number;
  unitExternalAirTemperatureInDegreesCelsius: number;
  wallElements: Array<WallElementDesignCalculationsInputs>;
  roofElements: Array<RoofElementDesignCalculationsInputs>;
  floorElements: Array<FloorElementDesignCalculationsInputs>;
  rooms: Array<RoomDesignCalculationsInputs>;
};

export type RoomDesignCalculationsInputs = {
  manifoldIndex: number;
  roomIndex: number;
  hasHotWaterUnderfloorHeating: boolean;
  hasElectricUnderfloorHeating: boolean;
  hasSkirtingHeating: boolean;
  hasRadiator: boolean;
  roomTemperatureInDegreesCelsius: number;
  maximumRoomTemperatureInDegreesCelsius: number;
  floorLevelCode: FloorLevelCode;
  roomAreaInMetresSquared: number;
  voidAreaInMetresSquared: number;
  unheatedAreaInMetresSquared: number;
  peripheralAreaInMetresSquared: number;
  ceilingHeightInMetres: number;
  numberOfAirChangesPerHour: number;
  mechanicalVentilationHeatRecoveryEfficiencyPercentage: number;
  floorCoveringThermalResistancePerMillimetre: number;
  floorCoveringThicknessInMillimetres: number;
  deckingThermalResistancePerMillimetre: number;
  deckingThicknessInMillimetres: number;
  underlayThermalResistancePerMillimetre: number;
  underlayThicknessInMillimetres: number;
  flowTemperatureInDegreesCelsius: number;
  temperatureDeltaInDegreesCelsius: number;
  mainFloorOutputFormulaDetails: FloorOutputFormulaDetails;
  peripheralFloorOutputFormulaDetails: FloorOutputFormulaDetails;
  screedConductivityInWattsPerMetreKelvin: number | null;
  thicknessOfScreedAbovePipeInMillimetres: number | null;
  userEnteredTotalHeatLossInWatts: number | null;
  pipeDetails: PipeDetails | null;
  walls: Array<RoomWallDesignCalculationsInputs>;
  roofs: Array<RoomRoofDesignCalculationsInputs>;
  floors: Array<RoomFloorDesignCalculationsInputs>;
};

export type WallElementDesignCalculationsInputs = {
  wallElementIndex: number;
  wallThermalTransmittance: number;
  windowThermalTransmittance: number;
  doorThermalTransmittance: number;
};

export type RoomWallDesignCalculationsInputs = {
  wallElementIndex: number;
  outsideWallLengthInMetres: number;
  additionalWallAreaInMetresSquared: number;
  windowAreaInMetresSquared: number;
  doorAreaInMetresSquared: number;
  externalAirTemperatureInDegreesCelsius: number;
};

export type RoofElementDesignCalculationsInputs = {
  roofElementIndex: number;
  roofThermalTransmittance: number;
  skylightThermalTransmittance: number;
};

export type RoomRoofDesignCalculationsInputs = {
  roofElementIndex: number;
  maximumVaultHeightInMetres: number;
  percentageOfRoomArea: number;
  skylightAreaInMetresSquared: number;
  temperatureAboveInDegreesCelsius: number;
  additionalRoomVolumeInMetresCubed: number;
};

export type FloorElementDesignCalculationsInputs = {
  floorElementIndex: number;
  floorThermalTransmittance: number;
};

export type RoomFloorDesignCalculationsInputs = {
  floorElementIndex: number;
  percentageOfRoomArea: number;
  downlossTypeCode: DownlossTypeCode;
  temperatureBelowInDegreesCelsius: number;
};

export type FloorOutputFormulaDetails = {
  formulaType: string | null;
  coefficientA: number | null;
  coefficientB: number | null;
  coefficientC: number | null;
  coefficientD: number | null;
  coefficientE: number | null;
  coefficientF: number | null;
  coefficientG: number | null;
};

export type PipeDetails = {
  floorConstructionPipeSpacingInMillimetres: number;
  floorConstructionPipeDiameterInMillimetres: number;
  estimatedLengthOfEachPipeInMetres: number;
  drawnLengthInMetres: number | null;
  verticalRunbackInMetres: number;
  numberOfPorts: number;
  pipesPerPort: number;
};
